import type { MailboxInfo } from 'owa-client-types';
import getFolderTable from '../selectors/getFolderTable';
import {
    ARCHIVE_FOLDER_ROOT_DISTINGUISHED_ID,
    ARCHIVE_DUMPSTER_DISTINGUISHED_ID,
} from 'owa-folders-constants';
import { getGlobalSettingsAccountMailboxInfo } from 'owa-account-source-list-store';
import { getMailboxInfoForArchive } from 'owa-mailbox-info/lib/getMailboxInfoForArchive';
import { getMailboxInfoForAuxArchive } from 'owa-mailbox-info/lib/getMailboxInfoForAuxArchive';
import { getMailboxInfoForCoprincipalAccount } from 'owa-mailbox-info/lib/getMailboxInfoForCoprincipalAccount';
import { getMailboxInfoForShared } from 'owa-mailbox-info/lib/getMailboxInfoForShared';
import type { MailFolder } from 'owa-graph-schema';
import { isMailboxSharedResourceArchive } from 'owa-account-shared-mailbox-utils';
import { isUnifiedInboxMailboxInfo } from 'owa-client-types/lib/UnifiedInboxMailboxInfo';

function getMailboxInfoForFolder(
    folderId: string,
    folder: MailFolder | undefined,
    mailboxInfo: MailboxInfo,
    routeToAuxIfAuxArchive: boolean
): MailboxInfo {
    let auxArchiveMailboxGuid;

    if (folder?.mailboxInfo.type === 'SharedMailbox') {
        return getMailboxInfoForShared(mailboxInfo, folder.mailboxInfo.mailboxSmtpAddress);
    } else if (
        folderId == ARCHIVE_FOLDER_ROOT_DISTINGUISHED_ID ||
        folderId == ARCHIVE_DUMPSTER_DISTINGUISHED_ID ||
        folder?.mailboxInfo.type == 'ArchiveMailbox'
    ) {
        // folderId check is required because when fetching the folder list for the archive, the root folder is not
        // yet present in the folder table
        auxArchiveMailboxGuid = folder?.replicaList?.[0];

        const isSharedArchive = folder && isMailboxSharedResourceArchive(folder?.mailboxInfo);
        // Most requests should be routed to the mailbox the folder is in.
        // However, for folder operations on auxilliary archive folders,
        // the request always needs to go to the main archive mailbox.
        if (auxArchiveMailboxGuid && routeToAuxIfAuxArchive) {
            // If it's a shared archive, we need to make sure that both the aux archive guid and the
            // shared archive information are used
            if (isSharedArchive) {
                return getMailboxInfoForAuxArchive(
                    mailboxInfo,
                    auxArchiveMailboxGuid,
                    undefined,
                    undefined,
                    folder.mailboxInfo.explicitLogonMailboxSmtpAddress || undefined
                );
            } else {
                return getMailboxInfoForAuxArchive(mailboxInfo, auxArchiveMailboxGuid);
            }
        }

        // If it's a shared archive folder, the mailboxInfo on the folder should already be correct, just return that
        return isSharedArchive ? folder.mailboxInfo : getMailboxInfoForArchive(mailboxInfo);
    } else if (isUnifiedInboxMailboxInfo(folder?.mailboxInfo)) {
        return folder.mailboxInfo;
    } else {
        return getMailboxInfoForCoprincipalAccount(mailboxInfo);
    }
}

/**
 * Fetches mailbox info for a given folder
 * @param folderId Id of the folder
 * @param routeToAuxIfAuxArchive whether to route the request to the the aux archive if the folder
 * belongs to aux archive mailbox (should only be false for some folder operations).
 */
export function getMailboxInfoForFolderId(
    folderId: string,
    routeToAuxIfAuxArchive: boolean
): MailboxInfo {
    const folderTable = getFolderTable();
    const folder = folderTable?.get(folderId);

    const mailboxInfo: MailboxInfo = folder?.mailboxInfo ?? getGlobalSettingsAccountMailboxInfo();
    return getMailboxInfoForFolder(folderId, folder, mailboxInfo, routeToAuxIfAuxArchive);
}
